import React, { useEffect, useState } from 'react'

import { css } from '@emotion/core'
import { Box, Container, Grid, Hidden } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'

import { Button, FormError, Header, Heading, Main, Typography } from '~/components'
import { Theme } from '~/styles'
import { useSetToken } from '~/utils'
import { useMutation } from '~/utils/useMutation'
import api from '~/api'

import EMAIL_VERIFICATION_IMAGE from './../assets/img/EmailVerification.png'
import { colors } from '~/styles/color'

const EmailVerification: React.FC = () => {
  const auth0 = useAuth0()
  const history = useHistory()
  const setToken = useSetToken()

  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!auth0.isLoading && !auth0.isAuthenticated) {
      auth0.loginWithRedirect({
        redirectUri: window.location.origin + '/emailverification',
      })
    }
  }, [auth0.isLoading, auth0.isAuthenticated])

  useEffect(() => {
    if (auth0.isAuthenticated) {
      setToken.handler()
    }
  }, [auth0.isAuthenticated])

  // "I verified my email" — refresh the Auth0 session so the latest
  // `email_verified` claim is picked up, then send the user to Landing, which
  // bootstraps the User (/user/authenticate/) and routes them onward.
  const verifyAndContinue = useMutation<[], void>(async () => {
    setErrorMessage(null)
    setStatusMessage(null)
    try {
      await auth0.getAccessTokenSilently({ ignoreCache: true })
      const claims = await auth0.getIdTokenClaims()
      if (!claims?.email_verified) {
        setErrorMessage(
          "We still don't see your email as verified. Please click the link in the email we sent you, then try again.",
        )
        return
      }
      history.push('/')
    } catch {
      setErrorMessage('Something went wrong. Please try again.')
    }
  })

  const resendEmail = useMutation<[], void>(async () => {
    setErrorMessage(null)
    setStatusMessage(null)
    try {
      await api.rest.post('/bff/web/email-resend/')
      setStatusMessage(`We just sent a new verification email to ${auth0.user?.email}.`)
    } catch {
      setErrorMessage("We couldn't send a new verification email. Please try again in a moment.")
    }
  })

  const getEmailVerificationContent = () => (
    <>
      <Hidden smDown>
        <Grid item md={6}>
          <Box mr={5} ml={-5}>
            <img css={styles.image} src={EMAIL_VERIFICATION_IMAGE} alt="Email Verification" />
          </Box>
        </Grid>
      </Hidden>
      <Grid item container md={6} alignItems="center">
        <Main mx={3}>
          <Heading>Verify your email address</Heading>
          <Typography variant="subText">
            We sent a verification link to{' '}
            <Typography css={styles.textstyle}>{auth0.user?.email}</Typography>.{' '}
            <Typography
              data-cy="change-email-toggle"
              css={styles.pressableText}
              onClick={() => history.push('/emailverification/change-email')}
            >
              Edit email
            </Typography>
          </Typography>

          {statusMessage && (
            <Box mt={3}>
              <Typography variant="subText">{statusMessage}</Typography>
            </Box>
          )}
          {errorMessage && (
            <Box mt={3}>
              <FormError>{errorMessage}</FormError>
            </Box>
          )}

          <Box mt={3}>
            <Button
              data-cy="i-verified"
              onClick={verifyAndContinue.handler}
              loading={verifyAndContinue.loading}
            >
              I&apos;ve verified my email
            </Button>
          </Box>
          <Box mt={2}>
            <Button
              data-cy="resend-email"
              onClick={resendEmail.handler}
              loading={resendEmail.loading}
              buttonColor={colors.gray[100]}
              textColor="#131D2B"
            >
              Resend verification email
            </Button>
          </Box>
        </Main>
      </Grid>
    </>
  )

  return (
    <>
      <Header />
      <Container>
        <Box mt={10}>
          <Grid container spacing={2}>
            {getEmailVerificationContent()}
          </Grid>
        </Box>
      </Container>
    </>
  )
}

const styles = {
  image: (theme: Theme) =>
    css({
      display: 'block',
      maxHeight: '50em',
      width: 'auto',
    }),
  title: (theme: Theme) =>
    css({
      fontWeight: 550,
      fontStyle: 'normal',
      fontSize: '3rem',
      [theme.mediaQueries.lg]: {
        fontSize: '3.6rem',
      },
    }),
  // Not sure how to make pseudo-element work with object syntax
  listHead: (theme: Theme) =>
    css({
      fontSize: '1.8rem',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      [theme.mediaQueries.lg]: {
        fontSize: '2.2rem',
      },
    }),
  listHeadImg: (theme: Theme) =>
    css({
      width: '2.5em',
      marginRight: '0.5em',
      [theme.mediaQueries.lg]: {
        width: '3em',
        marginLeft: '-4em',
        marginRight: '1em',
      },
    }),
  listSubhead: (theme: Theme) =>
    css({
      fontSize: '1.8rem',
    }),
  stores: (theme: Theme) =>
    css({
      display: 'flex',
    }),
  storesIcon: (theme: Theme) =>
    css({
      color: '#000',
      height: '4.25rem',
      margin: '1rem',
      marginLeft: 0,
      paddingLeft: 0,
    }),
  textstyle: (theme: Theme) =>
    css({
      display: 'inline',
      fontWeight: 'bold',
    }),
  pressableText: (theme: Theme) =>
    css({
      display: 'inline',
      textDecorationLine: 'underline',
      color: theme.colors.blue['700'],
      cursor: 'pointer',
    }),
  inlineText: (theme: Theme) =>
    css({
      display: 'inline',
    }),
}

export default EmailVerification
